/** BSC Escrow: approve + lockForCpc via MetaMask (no viem dependency). */

type EthereumProvider = {
	request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
	isMetaMask?: boolean;
	providers?: EthereumProvider[];
};

/** BSC BEP-652 / EIP-7825 per-tx gas cap (MetaMask RPC rejects above this). */
const BSC_MAX_TX_GAS = 16_000_000n;

function resolveEthereumProvider(): EthereumProvider {
	const eth = (window as Window & { ethereum?: EthereumProvider }).ethereum;
	if (!eth) throw new Error('未检测到 MetaMask 或其它 EVM 钱包');
	const providers = eth.providers?.filter(Boolean);
	if (providers?.length) {
		const mm = providers.find((p) => p.isMetaMask);
		if (mm) return mm;
		return providers[0]!;
	}
	return eth;
}

async function ethRequest<T>(method: string, params?: unknown[]): Promise<T> {
	return (await resolveEthereumProvider().request({ method, params })) as T;
}

import { normalizeToHexAddress } from './cpcAddress';

/** CPC address (`D…` 或 0x+64 hex) → bytes32 for escrow. */
export function cpcAddressToBytes32(cpcAddress: string): `0x${string}` {
	try {
		return normalizeToHexAddress(cpcAddress) as `0x${string}`;
	} catch {
		throw new Error('RWAX 地址须为 R… / D… 或 0x + 64 位 hex');
	}
}

export function parseTokenAmountToWei(amount: string, decimals: number): bigint {
	const trimmed = amount.trim();
	if (!trimmed || !/^\d+(\.\d+)?$/.test(trimmed)) {
		throw new Error('无效数量');
	}
	const [whole, frac = ''] = trimmed.split('.');
	const fracPadded = frac.padEnd(decimals, '0').slice(0, decimals);
	const combined = `${whole}${fracPadded}`.replace(/^0+/, '') || '0';
	return BigInt(combined);
}

function pad64(hex: string): string {
	return hex.replace(/^0x/, '').toLowerCase().padStart(64, '0');
}

function encodeApprove(spender: string, amount: bigint): string {
	const selector = '095ea7b3';
	return `0x${selector}${pad64(spender)}${amount.toString(16).padStart(64, '0')}`;
}

function encodeLockForCpc(cpcRecipient: `0x${string}`, amount: bigint): string {
	const selector = '2dda56bf'; // lockForCpc(bytes32,uint256)
	return `0x${selector}${pad64(cpcRecipient)}${amount.toString(16).padStart(64, '0')}`;
}

function encodeAllowance(owner: string, spender: string): string {
	const selector = 'dd62ed3e';
	return `0x${selector}${pad64(owner)}${pad64(spender)}`;
}

function encodeBalanceOf(owner: string): string {
	const selector = '70a08231';
	return `0x${selector}${pad64(owner)}`;
}

/** OpenZeppelin ERC20 custom errors (Solidity 0.8.20+). */
const ERR_INSUFFICIENT_BALANCE = 'e450d38c';
const ERR_INSUFFICIENT_ALLOWANCE = 'fb8f41b2';

function formatWeiAmount(wei: bigint, decimals: number): string {
	const base = 10n ** BigInt(decimals);
	const whole = wei / base;
	const frac = wei % base;
	if (frac === 0n) return whole.toString();
	const fracStr = frac.toString().padStart(decimals, '0').replace(/0+$/, '');
	return `${whole}.${fracStr}`;
}

function decodeRevertMessage(raw: string, decimals: number): string | null {
	const hex = raw.replace(/^execution reverted:\s*/i, '').trim();
	if (!hex.startsWith('0x') || hex.length < 10) return null;
	const selector = hex.slice(2, 10).toLowerCase();
	const payload = hex.slice(10);

	if (selector === ERR_INSUFFICIENT_BALANCE && payload.length >= 192) {
		const balance = BigInt(`0x${payload.slice(64, 128)}`);
		const needed = BigInt(`0x${payload.slice(128, 192)}`);
		return `Token 余额不足：需要 ${formatWeiAmount(needed, decimals)}，当前 ${formatWeiAmount(balance, decimals)}（授权额度不能代替余额，请先向钱包转入 token）`;
	}
	if (selector === ERR_INSUFFICIENT_ALLOWANCE && payload.length >= 192) {
		const allowance = BigInt(`0x${payload.slice(64, 128)}`);
		const needed = BigInt(`0x${payload.slice(128, 192)}`);
		return `Token 授权不足：需要 ${formatWeiAmount(needed, decimals)}，当前 allowance ${formatWeiAmount(allowance, decimals)}，请重新授权 Escrow`;
	}
	return null;
}

function gasHex(gas: bigint): string {
	return `0x${gas.toString(16)}`;
}

const RPC_TIMEOUT_MS = 20_000;

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
	return Promise.race([
		promise,
		new Promise<never>((_, reject) => {
			setTimeout(() => reject(new Error(`${label} 超时（${ms / 1000}s）`)), ms);
		}),
	]);
}

/** 优先走注入钱包（与 RWAX DApp 同一条 BSC RPC），避免 WebView 直打 GetBlock CORS/挂死。 */
async function rpcCall<T>(
	rpcUrl: string,
	method: string,
	params: unknown[],
): Promise<T> {
	const eth = (window as Window & { ethereum?: EthereumProvider }).ethereum;
	if (eth?.request) {
		try {
			return await withTimeout(
				eth.request({ method, params }) as Promise<T>,
				RPC_TIMEOUT_MS,
				`钱包 ${method}`,
			);
		} catch (e) {
			const msg = e instanceof Error ? e.message : String(e);
			if (/超时/.test(msg)) throw e;
			// 钱包未实现该方法时再走 HTTP
		}
	}

	const ctrl = new AbortController();
	const timer = setTimeout(() => ctrl.abort(), RPC_TIMEOUT_MS);
	try {
		const res = await fetch(rpcUrl, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ jsonrpc: '2.0', id: 1, method, params }),
			signal: ctrl.signal,
		});
		const json = (await res.json()) as { result?: T; error?: { message: string } };
		if (json.error) throw new Error(json.error.message);
		return json.result as T;
	} catch (e) {
		if (e instanceof DOMException && e.name === 'AbortError') {
			throw new Error(`BSC RPC ${method} 超时，请检查网络后重试`);
		}
		throw e;
	} finally {
		clearTimeout(timer);
	}
}

async function readAllowance(
	rpcUrl: string,
	token: string,
	owner: string,
	spender: string,
): Promise<bigint> {
	const result = await rpcCall<string>(rpcUrl, 'eth_call', [
		{ to: token, data: encodeAllowance(owner, spender) },
		'latest',
	]);
	if (!result || result === '0x') return 0n;
	return BigInt(result);
}

async function readBalance(rpcUrl: string, token: string, owner: string): Promise<bigint> {
	const result = await rpcCall<string>(rpcUrl, 'eth_call', [
		{ to: token, data: encodeBalanceOf(owner) },
		'latest',
	]);
	if (!result || result === '0x') return 0n;
	return BigInt(result);
}

/** Dry-run contract call; surfaces revert reason before MetaMask gas estimation blows up. */
async function simulateCall(
	rpcUrl: string,
	from: string,
	to: string,
	data: string,
	decimals: number,
): Promise<void> {
	try {
		await rpcCall(rpcUrl, 'eth_call', [{ from, to, data }, 'latest']);
	} catch (e) {
		const msg = e instanceof Error ? e.message : String(e);
		const decoded = decodeRevertMessage(msg, decimals);
		if (decoded) throw new Error(decoded);
		if (/insufficient allowance|transfer amount exceeds allowance/i.test(msg)) {
			throw new Error('Token 授权不足，请重新授权后再锁定');
		}
		if (/execution reverted/i.test(msg)) {
			throw new Error(`BSC 合约会拒绝此交易：${msg}`);
		}
		throw e;
	}
}

async function estimateGasLimit(
	rpcUrl: string,
	from: string,
	to: string,
	data: string,
	fallback: bigint,
	ceiling: bigint,
): Promise<bigint> {
	try {
		const raw = await rpcCall<string>(rpcUrl, 'eth_estimateGas', [{ from, to, data }]);
		const estimated = BigInt(raw);
		const buffered = (estimated * 12n) / 10n + 10_000n;
		const capped = buffered > ceiling ? ceiling : buffered;
		return capped > BSC_MAX_TX_GAS ? BSC_MAX_TX_GAS : capped;
	} catch {
		return fallback > BSC_MAX_TX_GAS ? BSC_MAX_TX_GAS : fallback;
	}
}

async function sendTx(
	from: string,
	to: string,
	data: string,
	gasLimit: bigint,
): Promise<string> {
	const hash = await ethRequest<string>('eth_sendTransaction', [
		{ from, to, data, gas: gasHex(gasLimit) },
	]);
	return hash;
}

async function waitReceipt(rpcUrl: string, txHash: string, maxMs = 120000): Promise<void> {
	const start = Date.now();
	while (Date.now() - start < maxMs) {
		const result = await rpcCall<{ status?: string } | null>(
			rpcUrl,
			'eth_getTransactionReceipt',
			[txHash],
		);
		if (result) {
			if (result.status === '0x0') throw new Error('BSC 交易失败（链上 revert）');
			return;
		}
		await new Promise((r) => setTimeout(r, 2000));
	}
	throw new Error('等待 BSC 交易确认超时');
}

export type LockUsdtForCpcParams = {
	from: string;
	tokenAddress: string;
	escrowAddress: string;
	cpcRecipient: string;
	amount: string;
	decimals: number;
	bscRpcUrl: string;
};

/** Approve (if needed) then lockForCpc on BSC mainnet Escrow. */
export async function lockUsdtForCpcOnBsc(params: LockUsdtForCpcParams): Promise<string> {
	const {
		from,
		tokenAddress,
		escrowAddress,
		cpcRecipient,
		amount,
		decimals,
		bscRpcUrl,
	} = params;
	const amountWei = parseTokenAmountToWei(amount, decimals);
	const recipientBytes32 = cpcAddressToBytes32(cpcRecipient);

	const balance = await readBalance(bscRpcUrl, tokenAddress, from);
	if (balance < amountWei) {
		throw new Error(
			`Token 余额不足：需要 ${formatWeiAmount(amountWei, decimals)}，当前 ${formatWeiAmount(balance, decimals)}。` +
				' 请向 BSC 钱包转入 BNB USDT（`0x55d3…7955`）后再锁定；仅有授权额度无法扣款。',
		);
	}

	const allowance = await readAllowance(bscRpcUrl, tokenAddress, from, escrowAddress);
	if (allowance < amountWei) {
		const approveData = encodeApprove(escrowAddress, amountWei);
		await simulateCall(bscRpcUrl, from, tokenAddress, approveData, decimals);
		const approveGas = await estimateGasLimit(
			bscRpcUrl,
			from,
			tokenAddress,
			approveData,
			100_000n,
			200_000n,
		);
		const approveHash = await sendTx(from, tokenAddress, approveData, approveGas);
		await waitReceipt(bscRpcUrl, approveHash);

		const allowanceAfter = await readAllowance(bscRpcUrl, tokenAddress, from, escrowAddress);
		if (allowanceAfter < amountWei) {
			throw new Error('授权已确认但额度仍不足，请检查 token 是否为标准 ERC-20');
		}
	}

	const lockData = encodeLockForCpc(recipientBytes32, amountWei);
	await simulateCall(bscRpcUrl, from, escrowAddress, lockData, decimals);
	const lockGas = await estimateGasLimit(
		bscRpcUrl,
		from,
		escrowAddress,
		lockData,
		150_000n,
		350_000n,
	);
	const lockHash = await sendTx(from, escrowAddress, lockData, lockGas);
	await waitReceipt(bscRpcUrl, lockHash);
	return lockHash;
}
